import { validateEmail, validateMobile } from '@/utils/validators'

/**
 * Channel rules for the notification preferences modal of Users Management,
 * where a manager picks the notification types a recipient gets by email, SMS
 * and push.
 *
 * KPT-1186: the backend sends on a channel only while both the recipient's
 * channel switch (`emailEnabled`, `mobileEnabled`, `pushNotificationsEnabled`)
 * and the type's switch for that channel are on. Saving the modal set every
 * channel switch from the type switches, so a manager could turn on email for
 * a user who had it off, or SMS for a user with no mobile number. A manager
 * now only picks types on a channel the recipient already has on, with an
 * address to send to: a valid email address or mobile number (KPT-1187).
 *
 * The sync runs one way, from channel to types: a channel that is off keeps
 * none of its types on, and saving the modal only stores the types.
 */

/**
 * Whether the manager may toggle the email switches of the notification
 * types: only while the recipient has email on and a valid email address.
 * KPT-1187: email on with an empty address used to unlock them.
 * @param {object} settings one recipient of `notificationSettings`
 * @returns {boolean}
 */
export function canToggleEmailPreferences (settings) {
  return Boolean(settings?.emailEnabled) && validateEmail(settings?.email)
}

/**
 * Whether the manager may toggle the SMS switches of the notification types:
 * only while the recipient has mobile on and a valid mobile number.
 * @param {object} settings one recipient of `notificationSettings`
 * @returns {boolean}
 */
export function canToggleSmsPreferences (settings) {
  return Boolean(settings?.mobileEnabled) && validateMobile(settings?.mobileNumber)
}

/**
 * The recipient's notification types with every type of a channel that is off
 * switched off as well, so the modal never shows, nor saves, types on for a
 * channel that sends nothing. The SMS types follow the Mobile switch; push
 * types are left alone, as managers cannot change push.
 * @param {object} settings one recipient of `notificationSettings`
 * @returns {object|undefined} a new `notificationsPreferences`, or the
 *   recipient's own when it has none
 */
export function withDisabledChannelTypesOff (settings) {
  const preferences = settings?.notificationsPreferences
  if (!preferences) {
    return preferences
  }
  const off = {}
  if (!settings.emailEnabled) {
    off.emailEnabled = false
  }
  if (!settings.mobileEnabled) {
    off.smsEnabled = false
  }
  const cleared = {}
  Object.keys(preferences).forEach((type) => {
    cleared[type] = { ...preferences[type], ...off }
  })
  return cleared
}
